<template>
  <form
    :id="id ?? 'feedback-form'"
    ref="form"
    novalidate
    class="d-flex flex-column needs-validation"
    @submit="submit"
  >
    <div id="feedback.form.selection" class="relative d-flex flex-column mb-3">
      <label class="form-label">
        {{ translate('Would you like to send feedback or report a problem?') }}
        <span class="text-danger">*</span>
      </label>
      <BootstrapRadio
        id="feedback"
        v-model="modelType"
        value="feedback"
        :label="translate('Provide feedback')"
        name="type"
        required
        data-testid="feedback.form.radio.provides-feedback"
      />
      <BootstrapRadio
        id="problem"
        v-model="modelType"
        value="problem"
        :label="translate('Report a problem')"
        name="type"
        required
        data-testid="feedback.form.radio.report-a-problem"
      />
    </div>

    <BootstrapFormControl
      v-model="modelTitle"
      :name="translate('Title')"
      :placeholder="translate('Enter a brief title')"
      :max-length="100"
      class="mb-3"
      required
      :error="errorMessages.get('title')"
      :server-error="serverError"
      data-testid="feedback.form.input.title"
    />

    <div v-if="type === 'problem'">
      <BootstrapSelect
        v-model="modelProblemType"
        :name="translate('Type')"
        :options="problemTypeOptions"
        :placeholder="translate('Select problem type')"
        class="mb-3"
        required
        :error="errorMessages.get('problem_type')"
        :server-error="serverError"
        data-testid="feedback.form.select.problem-type"
      />

      <BootstrapFormControl
        v-model="modelPageUrl"
        :name="translate('Page URL')"
        :placeholder="translate('Enter page URL')"
        class="mb-3"
        required
        :error="errorMessages.get('page_url')"
        :server-error="serverError"
        data-testid="feedback.form.input.page-url"
      />
    </div>

    <BootstrapFormControl
      v-model="modelSummary"
      :name="translate('Summary')"
      :max-length="500"
      :placeholder="summaryPlaceholder"
      class="mb-3"
      textarea
      required
      :error="errorMessages.get('feedback')"
      :server-error="serverError"
      data-testid="feedback.form.input.summary"
    />

    <BootstrapAlert variant="primary" data-testid="feedback.form.alert.message">
      <Icon icon="material-symbols:info-outline" class="flex-shrink-0 me-3" :inline="true" />
      <p class="m-0">
        {{ translate('Please contact support if you require further assistance.') }}
      </p>
    </BootstrapAlert>
  </form>
</template>

<script lang="ts" setup>
import {useLocalisation} from '@/composables/useLocalisation';

const props = defineProps<{
  id?: string;
  errorMessages: Map<any, any>;
  loading: boolean;
  pageUrl: string;
  problemType: string;
  problemTypeOptions: string[];
  serverError: boolean;
  summary: string;
  title: string;
  type: string;
}>();

const emit = defineEmits<{
  'update:pageUrl': [value: string];
  'update:problemType': [value: string];
  'update:summary': [value: string];
  'update:title': [value: string];
  'update:type': [value: string];
  submit: [form: globalThis.Ref<HTMLFormElement | null>];
}>();

const {translate} = useLocalisation();

const form = ref<HTMLFormElement | null>(null);

const modelType = computed({
  get: () => props.type,
  set: (value) => emit('update:type', value),
});

const modelTitle = computed({
  get: () => props.title,
  set: (value) => emit('update:title', value),
});

const modelProblemType = computed({
  get: () => props.problemType,
  set: (value) => emit('update:problemType', value),
});

const modelPageUrl = computed({
  get: () => props.pageUrl,
  set: (value) => emit('update:pageUrl', value),
});

const modelSummary = computed({
  get: () => props.summary,
  set: (value) => emit('update:summary', value),
});

const summaryPlaceholder = computed(() => {
  if (props.type === 'problem') {
    return translate('Describe the problem you are experiencing...');
  }
  return translate('Share your feedback with us...');
});

const submit = async (event: Event) => {
  if (form.value?.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.value.classList.add('was-validated');
  } else {
    event.preventDefault();
    emit('submit', form);
  }
};
</script>
